import React, { useEffect, useState } from 'react';
import { Modal, Table, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getAssignmentStudents } from 'services/Teacher/Assignments.service';

// Per-student progress on an assignment. Keys match the statuses the
// assignment students endpoint derives from Submission/AttemptTracking.
export const STUDENT_STATUS_STYLES = {
    "not-started": { label: "Not started", bg: "#EFEFF4", color: "#6B7280" },
    "in-progress": { label: "In progress", bg: "#FFF3E2", color: "#D9822B" },
    submitted: { label: "Submitted", bg: "#E6F7EF", color: "#1B9C63" },
    "awaiting-revision": { label: "Awaiting revision", bg: "#E7F0FE", color: "#2F6FE4" },
    interrupted: { label: "Interrupted", bg: "#FDE8EC", color: "#E4415E" },
};

const StatusPill = ({ status }) => {
    const { t } = useTranslation();
    const style =
        STUDENT_STATUS_STYLES[status] || STUDENT_STATUS_STYLES["not-started"];
    return (
        <span
            style={{
                display: "inline-block",
                padding: "4px 12px",
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                backgroundColor: style.bg,
                color: style.color,
            }}
        >
            {t(style.label)}
        </span>
    );
};

// `assignmentId` drives the fetch — the roster and every student's status come
// from the API, never from the list row, so no stale status is ever shown.
const ViewStudentsModal = ({
    show,
    onClose,
    assignmentId,
    assignmentTitle,
}) => {
    const { t } = useTranslation();
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!show || !assignmentId) return;
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setFailed(false);
            try {
                const response = await getAssignmentStudents(assignmentId);
                // showOne() wraps the payload in `result`; tolerate `results`
                // too so this keeps working if the endpoint ever switches.
                const payload =
                    response?.data?.result || response?.data?.results;
                const results = payload?.students;
                if (cancelled) return;
                if (Array.isArray(results)) {
                    setStudents(results);
                } else {
                    setFailed(true);
                }
            } catch (error) {
                console.error("Error fetching assignment students:", error);
                if (!cancelled) setFailed(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        // Start empty rather than seeding from the row: those records carry no
        // status, so showing them first would flash a wrong "Not started" pill
        // before the real statuses arrive.
        setStudents([]);
        load();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, assignmentId]);

    return (
        <Modal show={show} onHide={onClose} size='lg'>
            <Modal.Header closeButton>
                <div>
                    <Modal.Title>{t("Assigned Students")}</Modal.Title>
                    {/* Titles repeat across assignments, so name the one in view. */}
                    {assignmentTitle && (
                        <div className="text-muted" style={{ fontSize: "13px" }}>
                            {assignmentTitle}
                            {!loading && students.length > 0 &&
                                ` · ${students.length} ${t("students")}`}
                        </div>
                    )}
                </div>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div
                        className="d-flex flex-column align-items-center justify-content-center text-muted py-5"
                    >
                        <Spinner animation="border" size="sm" />
                        <div className="mt-2" style={{ fontSize: "13px" }}>
                            {t("Loading students...")}
                        </div>
                    </div>
                ) : failed ? (
                    <p className="text-danger text-center py-5 mb-0">
                        {t("Could not load students. Please try again.")}
                    </p>
                ) : students.length > 0 ? (
                    // Scroll on the wrapper, not the table: a height on <table>
                    // stretches the rows to fill it instead of scrolling.
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                        <Table hover className="mb-0 align-middle">
                            <thead
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    background: "#F7F7FB",
                                    zIndex: 1,
                                }}
                            >
                                <tr>
                                    <th
                                        style={{
                                            width: "28%",
                                            fontSize: "13px",
                                            color: "#6B7280",
                                            fontWeight: 600,
                                            borderBottom: "1px solid #E9E9F0",
                                            padding: "10px 12px",
                                        }}
                                    >
                                        {t("Name")}
                                    </th>
                                    <th
                                        style={{
                                            fontSize: "13px",
                                            color: "#6B7280",
                                            fontWeight: 600,
                                            borderBottom: "1px solid #E9E9F0",
                                            padding: "10px 12px",
                                        }}
                                    >
                                        {t("Email")}
                                    </th>
                                    <th
                                        style={{
                                            width: "1%",
                                            whiteSpace: "nowrap",
                                            fontSize: "13px",
                                            color: "#6B7280",
                                            fontWeight: 600,
                                            borderBottom: "1px solid #E9E9F0",
                                            padding: "10px 12px",
                                        }}
                                    >
                                        {t("Status")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((student) => (
                                    <tr key={student._id}>
                                        <td
                                            style={{
                                                fontSize: "14px",
                                                fontWeight: 600,
                                                color: "#303972",
                                                padding: "10px 12px",
                                            }}
                                        >
                                            {student.fullName}
                                        </td>
                                        <td
                                            style={{
                                                fontSize: "13px",
                                                color: "#8C8C9A",
                                                wordBreak: "break-all",
                                                padding: "10px 12px",
                                            }}
                                        >
                                            {student.email}
                                        </td>
                                        <td
                                            style={{
                                                whiteSpace: "nowrap",
                                                padding: "10px 12px",
                                            }}
                                        >
                                            <StatusPill status={student.status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                ) : (
                    <p className="text-muted text-center py-5 mb-0">
                        {t("No students assigned to this assignment.")}
                    </p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    {t("Close")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ViewStudentsModal;
